import "./Mario.css";
import MarioCharacter from "../../../assets/img/gif/mario-run.gif";
import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import jumpAudio from "../../../assets/audio/mario-jump.mp3";
import backgroundMusic from "../../../assets/audio/running-about.mp3";

import { useDispatch, useSelector } from "react-redux";
import {
  marioJumping,
  marioHeight,
  marioLeft,
  marioTop,
  marioWidth,
  shrinkMarioSizeOnHit,
  increaseMarioSize,
  resetMarioSize,
  setInvincible,
} from "../../../config/redux/marioSlice";
import {
  setReady,
  setDie,
  setScore,
  incrementScore,
  setSpeed,
} from "../../../config/redux/engineSlice";
import dieAudio from "../../../assets/audio/mario-died.mp3";

// Remove Swal import since we're creating custom popup
// import Swal from "sweetalert2";

const Mario = ({ handleApiResponse, isMuted, onGameOverStateChange }) => {
  const marioRef = useRef();
  const dispatch = useDispatch();
  const invincibleTimerRef = useRef(null);
  const justHitRef = useRef(false);
  const scoreSaved = useRef(false);
  const collisionCheckRef = useRef(null);
  const deathSoundPlayed = useRef(false);

  // Add state for custom game over popup
  const [showGameOver, setShowGameOver] = useState(false);

  const die = useSelector((state) => state.engine.die);
  const loadingScreen = useSelector((state) => state.engine.loadingScreen);
  const isPlay = useSelector((state) => state.engine.play);
  const mario_jump = useSelector((state) => state.mario.jumping);
  const mario_height = useSelector((state) => state.mario.height);
  const mario_left = useSelector((state) => state.mario.left);
  const mario_top = useSelector((state) => state.mario.top);
  const mario_width = useSelector((state) => state.mario.width);
  const marioSize = useSelector((state) => state.mario.size);
  const invincible = useSelector((state) => state.mario.invincible);
  const score = useSelector((state) => state.engine.score);
  const speed = useSelector((state) => state.engine.speed);

  const obs1_height = useSelector((state) => state.obstacle.obs1Height);
  const obs1_left = useSelector((state) => state.obstacle.obs1Left);
  const obs1_top = useSelector((state) => state.obstacle.obs1Top);
  const obs1_width = useSelector((state) => state.obstacle.obs1Width);
  const obs2_height = useSelector((state) => state.obstacle.obs2Height);
  const obs2_left = useSelector((state) => state.obstacle.obs2Left);
  const obs2_top = useSelector((state) => state.obstacle.obs2Top);
  const obs2_width = useSelector((state) => state.obstacle.obs2Width);

  const jump = useMemo(() => new Audio(jumpAudio), []);
  const marioDie = useMemo(() => new Audio(dieAudio), []);
  const bgMusic = useMemo(() => new Audio(backgroundMusic), []);
  const scoredObs1 = useRef(false);
  const scoredObs2 = useRef(false);
  const isRestarting = useRef(false);
  // Utility function to reset all audio
  const resetAllAudio = () => {
    jump.pause();
    marioDie.pause();
    bgMusic.pause();
    jump.currentTime = 0;
    marioDie.currentTime = 0;
    bgMusic.currentTime = 0;
  };

  // Function to handle restart game
  const handleRestart = () => {
    // Prevent rapid clicking
    if (isRestarting.current) return;
    isRestarting.current = true;
    console.log("Restart button clicked!"); // Debug log
    // Reset all audio
    resetAllAudio();
    setShowGameOver(false);
    // Reset engine states
    dispatch(setDie(false));
    dispatch(setReady(false)); // This sets play to false
    dispatch(setScore(0));
    dispatch(setSpeed(-speed)); // Reset speed to 0 by subtracting current speed
    // Reset Mario states
    dispatch(resetMarioSize());
    dispatch(marioJumping(false));
    dispatch(setInvincible(false));
    // Reset local refs
    scoreSaved.current = false;
    deathSoundPlayed.current = false;
    scoredObs1.current = false;
    scoredObs2.current = false;

    // Allow restarting again after a short delay
    setTimeout(() => {
      isRestarting.current = false;
    }, 500);
  };

  useEffect(() => {
    if (showGameOver) {
      resetAllAudio();
    }
  }, [showGameOver]);

  // Update audio muted state when isMuted prop changes
  useEffect(() => {
    jump.muted = isMuted;
    marioDie.muted = isMuted;
    bgMusic.muted = isMuted;
  }, [isMuted, jump, marioDie, bgMusic]);

  const handleKey = useCallback(
    (e) => {
      if (e.code === "Enter" && !isPlay && !die && !loadingScreen) {
        dispatch(setReady(true));
      }
      if (
        mario_jump === false &&
        e.code === "Space" &&
        isPlay &&
        !die &&
        !loadingScreen
      ) {
        dispatch(marioJumping(true));

        // Play jump sound and reset it to beginning for next use
        if (!isMuted) {
          jump.currentTime = 0;
          jump.play().catch((error) => {
            console.log("Jump sound play failed:", error);
          });
        }

        setTimeout(() => {
          dispatch(marioJumping(false));
        }, 400);
      }
    },
    [mario_jump, jump, dispatch, isPlay, die, loadingScreen, isMuted]
  );

  // Improved collision detection function
  const checkCollision = useCallback(() => {
    if (!isPlay || die || invincible) return;

    // Debug: Log current state every 50 collisions (to avoid spam)
    if (Math.random() < 0.02) {
      // 2% chance to log
      console.log(
        "Collision check - isPlay:",
        isPlay,
        "die:",
        die,
        "invincible:",
        invincible,
        "marioSize:",
        marioSize
      );
    }

    const handleCollisionHit = () => {
      if (invincible) return;

      console.log("Collision detected! Game Over!"); // Debug log

      // Any collision ends the game immediately
      if (scoreSaved.current) return;
      scoreSaved.current = true;

      dispatch(setDie(true));

      // Only play death sound once and stop other audio
      if (!deathSoundPlayed.current) {
        // Stop all audio and reset positions
        resetAllAudio();

        // Play death sound
        if (!isMuted) {
          marioDie.play().catch(() => {});
        }
        deathSoundPlayed.current = true;
      }

      dispatch(setReady(false));

      if (handleApiResponse) {
        handleApiResponse(score);
      }

      setTimeout(() => {
        // Show custom game over popup
        setShowGameOver(true);
      }, 500);
    };

    // Improved collision detection with better bounds checking
    const isCollided = (obsLeft, obsTop, obsWidth, obsHeight) => {
      // Only check collision if obstacle is on screen and Mario has valid dimensions
      if (
        obsLeft < 0 ||
        obsLeft > window.innerWidth ||
        mario_width <= 0 ||
        mario_height <= 0 ||
        obsWidth <= 0 ||
        obsHeight <= 0
      ) {
        return false;
      }

      // Check if Mario is jumping - if so, adjust collision bounds
      const marioBottom = mario_top + mario_height;
      const marioRight = mario_left + mario_width;
      const obsBottom = obsTop + obsHeight;
      const obsRight = obsLeft + obsWidth;

      // If Mario is jumping, he should be able to pass over obstacles
      if (mario_jump) {
        // Check if Mario's bottom is above the obstacle's top (successful jump)
        if (marioBottom <= obsTop) {
          return false;
        }
      }

      // Standard collision detection
      return (
        mario_left < obsRight &&
        marioRight > obsLeft &&
        mario_top < obsBottom &&
        marioBottom > obsTop
      );
    };

    // Check collision with obstacle 1
    if (isCollided(obs1_left, obs1_top, obs1_width, obs1_height)) {
      console.log("Collision with obstacle 1 detected"); // Debug log
      handleCollisionHit();
    } else if (obs1_left + obs1_width < mario_left && !scoredObs1.current) {
      dispatch(incrementScore(5));
      scoredObs1.current = true;
    }

    // Check collision with obstacle 2
    if (isCollided(obs2_left, obs2_top, obs2_width, obs2_height)) {
      console.log("Collision with obstacle 2 detected"); // Debug log
      handleCollisionHit();
    } else if (obs2_left + obs2_width < mario_left && !scoredObs2.current) {
      dispatch(incrementScore(5));
      scoredObs2.current = true;
    }

    // Reset scoring flags when obstacles are off screen
    if (obs1_left > window.innerWidth) scoredObs1.current = false;
    if (obs2_left > window.innerWidth) scoredObs2.current = false;
  }, [
    isPlay,
    die,
    invincible,
    marioSize,
    dispatch,
    marioDie,
    score,
    handleApiResponse,
    mario_left,
    mario_top,
    mario_width,
    mario_height,
    mario_jump,
    obs1_left,
    obs1_top,
    obs1_width,
    obs1_height,
    obs2_left,
    obs2_top,
    obs2_width,
    obs2_height,
  ]);

  // Set up collision checking interval
  useEffect(() => {
    if (!isPlay) {
      if (collisionCheckRef.current) {
        clearInterval(collisionCheckRef.current);
        collisionCheckRef.current = null;
      }
      return;
    }

    // Start collision checking when game is playing
    collisionCheckRef.current = setInterval(checkCollision, 50); // Check every 50ms

    return () => {
      if (collisionCheckRef.current) {
        clearInterval(collisionCheckRef.current);
        collisionCheckRef.current = null;
      }
    };
  }, [isPlay, checkCollision]);

  useEffect(() => {
    if (justHitRef.current) return;
    // Removed size increase logic - Mario stays at Size 0 always
  }, [score, marioSize, dispatch]);

  // Separate effect for background music management
  useEffect(() => {
    if (isPlay) {
      // Only start background music if it's not already playing
      if (bgMusic.paused) {
        bgMusic.loop = true;
        bgMusic.play().catch((error) => {
          console.log("Background music play failed:", error);
        });
      }
    } else {
      // Only pause if it's currently playing
      if (!bgMusic.paused) {
        bgMusic.pause();
        bgMusic.currentTime = 0;
      }
    }

    return () => {
      // Cleanup background music when component unmounts
      if (bgMusic) {
        bgMusic.pause();
        bgMusic.currentTime = 0;
      }
    };
  }, [isPlay, bgMusic]);

  useEffect(() => {
    document.addEventListener("keydown", handleKey);

    if (marioRef.current) {
      try {
        const rect = marioRef.current.getBoundingClientRect();
        dispatch(marioHeight(rect.height));
        dispatch(marioLeft(rect.left));
        dispatch(marioTop(rect.top));
        dispatch(marioWidth(rect.width));
      } catch (error) {
        console.log("Initial position update skipped - element not available");
      }
    }

    return () => {
      document.removeEventListener("keydown", handleKey);
      if (invincibleTimerRef.current) {
        clearTimeout(invincibleTimerRef.current);
      }
    };
  }, [handleKey, dispatch]);

  // Separate effect for continuous position updates
  useEffect(() => {
    if (!isPlay || !marioRef.current) return;

    const updatePosition = () => {
      if (marioRef.current) {
        try {
          const rect = marioRef.current.getBoundingClientRect();
          dispatch(marioHeight(rect.height));
          dispatch(marioLeft(rect.left));
          dispatch(marioTop(rect.top));
          dispatch(marioWidth(rect.width));
        } catch (error) {
          // Handle case where element is no longer available
          console.log("Position update skipped - element not available");
        }
      }
    };

    const intervalId = setInterval(updatePosition, 100);

    return () => {
      clearInterval(intervalId);
    };
  }, [isPlay, dispatch]);

  // Add cleanup effect for component unmount
  useEffect(() => {
    return () => {
      // Cleanup all intervals and timers when component unmounts
      if (collisionCheckRef.current) {
        clearInterval(collisionCheckRef.current);
        collisionCheckRef.current = null;
      }
      if (invincibleTimerRef.current) {
        clearTimeout(invincibleTimerRef.current);
        invincibleTimerRef.current = null;
      }
    };
  }, []);

  // Notify parent component when game over state changes
  useEffect(() => {
    if (onGameOverStateChange) {
      onGameOverStateChange(showGameOver);
    }
  }, [showGameOver, onGameOverStateChange]);

  return (
    <div className="mario-container">
      <img
        src={MarioCharacter}
        alt="Mario"
        className={`mario size-${marioSize} ${die ? "die" : ""} ${
          mario_jump ? "jump" : ""
        } ${invincible ? "invincible" : ""}`}
        ref={marioRef}
      />
      {/* Custom Game Over Popup */}
      {showGameOver && (
        <div
          className="game-over-overlay"
          style={{ pointerEvents: "auto" }}
          onClick={(e) => {
            console.log("Overlay clicked", e.target);
            // Prevent clicks on overlay from closing the popup
            e.stopPropagation();
          }}
        >
          <div className="game-over-popupmario">
            <div className="game-over-header">
              <h1 className="game-over-title">GAME OVER!</h1>
              <div className="game-over-icon">💀</div>
            </div>
            <div className="game-over-body">
              <p className="final-score-text">Token Earned</p>
              <div className="score-displaymario">
                <img
                  style={{ width: "35px" }}
                  src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                  alt="logo"
                />
                <p className="score-display-text">{score}</p>
              </div>
              {/* <p className="game-over-messagemario">Better luck next time!</p> */}
            </div>
            <div className="game-over-footer">
              <button
                className="restart-button"
                onClick={handleRestart}
                onMouseDown={() => console.log("Button mouse down")}
                onTouchStart={() => console.log("Button touch start")}
                style={{ pointerEvents: "auto", zIndex: 1001 }}
              >
                <span className="button-text">Play Again</span>
                {/* <span className="button-icon">🎮</span> */}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mario;
